import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";
import type { CreateEntityGroup } from "~pages/EntityGroups/types/upsertEntityGroup.ts";
import type { EntityGroup, EntityGroupResponse } from "~types/entityGroup.ts";

export const useCreateEntityGroup = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const request = useApi();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation<EntityGroup, Error, CreateEntityGroup>({
    mutationFn: async (data) => {
      const response = await request<EntityGroupResponse>(`${sport}/o/${organizationId}/entityGroups`, {
        method: "POST",
        body: data,
      });

      return response?.data?.[0];
    },
    onSuccess: async (createdEntityGroup) => {
      if (!createdEntityGroup?.entityGroupId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        if (updatedScope.resources.entityGroups) {
          updatedScope.resources.entityGroups[createdEntityGroup.entityGroupId] = createdEntityGroup;
        } else {
          updatedScope.resources.entityGroups = {
            [createdEntityGroup.entityGroupId]: createdEntityGroup,
          };
        }

        updatedScope.scope.entityGroupTree.push({
          entityGroupId: createdEntityGroup.entityGroupId,
          entityIds: [],
        });
      });

      await Promise.all([invalidateCache(), queryClient.invalidateQueries({ queryKey: ["teamGroups", organizationId] })]);
    },
  });
};
